import React, { ChangeEvent } from 'react';
import { Box, Checkbox, CheckboxGroup, IconButton } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { FieldOption, FieldOptionType } from 'components/FieldOption';
import { AddItemIcon } from 'components/icons';
import uniqueId from 'lodash/uniqueId';

export type CheckboxesInputType = {
  options: readonly FieldOptionType[];
};

interface CheckboxesInputProps {
  isDisabled?: boolean;
  value: CheckboxesInputType;
  onChange: (checkboxesDefinition: CheckboxesInputType) => void;
}

export const CheckboxesInput = ({
  value,
  onChange,
  isDisabled,
}: CheckboxesInputProps) => {
  const handleCheckboxOptionChange = (
    event: ChangeEvent<HTMLInputElement>,
    option: FieldOptionType,
  ) => {
    /* Deep copy of definition.options */
    const options = value.options.map((o: FieldOptionType) => ({
      value: o.value,
      text: o.text,
    }));

    const optionIndex = options.findIndex(
      (o: FieldOptionType) => o.value === option.value,
    );
    options[optionIndex].text = event.target.value;

    onChange({ options });
  };

  const handleCheckboxOptionRemoval = (option: FieldOptionType) => {
    const options = value.options.filter(
      (o: FieldOptionType) => o.value !== option.value,
    );

    onChange({ options });
  };

  const handleCheckboxOptionCreation = () => {
    const options = [...value.options, { value: uniqueId(), text: '' }];
    onChange({ options });
  };

  return (
    <Box>
      <CheckboxGroup>
        {value.options.map((option: FieldOptionType) => (
          <FieldOption
            placeholder={t`Add an option`}
            key={option.value}
            leftInput={<Checkbox isDisabled={isDisabled} />}
            option={option}
            isDisabled={!!isDisabled}
            handleFieldOptionChange={handleCheckboxOptionChange}
            handleFieldOptionRemoval={handleCheckboxOptionRemoval}
          />
        ))}
      </CheckboxGroup>

      <IconButton
        m={4}
        aria-label={t`Add new checkbox option`}
        icon={<AddItemIcon />}
        colorScheme="gray"
        borderRadius="full"
        isDisabled={isDisabled}
        onClick={handleCheckboxOptionCreation}
      />
    </Box>
  );
};
